/**
 * The one thing to do next.
 *
 * The dashboard used to open with eight things competing for the top of the
 * page: Start Here, the pick-up row, the lab nudge, a profile banner, a
 * location nudge, the weekly Q and A, an upcoming call and a trial
 * celebration. Three of them sat ABOVE the coach's own name. A coach could be
 * five banners deep before the page had said anything, and the feedback that
 * started all of this was the word "overwhelming".
 *
 * They are candidates now rather than blocks: this ranks them and the page
 * shows the first one, with the rest stacked behind it.
 *
 * THE ORDER IS COLEMAN'S (2026-10-07), top to bottom:
 *
 *   1. a failed payment
 *   2. the next Start Here step, in week one
 *   3. something new: a masterclass, the weekly Q and A, an X and O
 *      breakdown, a batch of drills
 *   4. a call in the next 24 hours (Coleman, 2026-10-22: "call today
 *      (any time in next 24 hours) should be a card above add to profile");
 *      within half an hour it's urgent and can't be skipped
 *   5. an empty profile
 *   6. certification, only when it has gone untouched for a week
 *   7. a masterclass they started and did not finish
 *   8. a part of the platform they have never opened, at most once every
 *      few days (moved from 5 to last, 2026-10-07: it was landing on top)
 *
 * This week's drop sits above all of it until it's opened or closed
 * (2026-10-22).
 *
 * "Add where you coach" was cut. Change the order here and in the test, which
 * holds it, together.
 *
 * Two rules it follows whatever the order:
 *
 *   A REASON IS REAL OR ABSENT. A card says why it is here only when the
 *   why is something the title does not already say, in the coach's terms:
 *   "3 pages from the end" is a reason, "Worth knowing about before the day
 *   gets away from you" is filler, and a coach who reads two of those stops
 *   reading the slot. No why beats a weak one.
 *
 *   THE FACE CARRIES A FACT WHEN THERE IS ONE. A countdown for a call, a
 *   ring for anything with progress, a count for anything new. The icon
 *   tile is the fallback for cards with no number in them.
 *
 * Pure, and with no imports, so it can be tested without rendering anything
 * and without dragging mockData into any bundle that wants it. Every fact it
 * reads is gathered by the caller and handed in; this file never fetches,
 * never reads localStorage and never knows what a component is.
 *
 * No em dashes.
 */

/** Inside this, a call is a deadline. */
export const CALL_URGENT_MS = 30 * 60 * 1000;

/** Outside this, a call is trivia. "Next call, in 5d 3h" is not a nudge. */
export const CALL_SOON_MS = 24 * 60 * 60 * 1000;

/** A masterclass barely opened is not "half done", it is a page you landed on. */
export const RESUME_MIN_PAGES = 2;

/** The same idea for progress that only ever stored a percentage. */
export const RESUME_MIN_PCT = 15;

/** Nothing is "new since you joined" to somebody who joined on Tuesday. */
export const SETTLING_IN_DAYS = 7;

/** New stays new for this long, and no longer. */
export const NEW_FOR_DAYS = 10;

/** One new drill is not news. A handful landing at once is. */
export const DRILL_BATCH_MIN = 3;

/** Certification only nudges once it has gone quiet for this long. */
export const CERT_QUIET_DAYS = 7;

/**
 * The shape of a candidate, which is also the shape the card renders from.
 *
 *   id          stable, so a dismissal can be remembered
 *   kind        what sort of thing it is, for analytics and for the icon
 *   eyebrow     two or three words above the title
 *   title       what the coach would call the thing
 *   why         one line saying why, or null when the title already says it
 *   cta         the button's words, a verb
 *   to          where the button goes: a nav id, or an action name the page knows
 *   art         optional cover image url
 *   face        optional fact for the card's face:
 *                 { type: "count", big, small }        a number and its unit
 *                 { type: "ring", pct, big, small }    progress, 0 to 1
 *   urgent      a deadline or money: drawn loud, everything else is quiet
 *   sticky      cannot be swiped away
 */

const day = 24 * 60 * 60 * 1000;

/** Whole days since a timestamp, or null when there isn't one. */
function daysSince(when, now) {
  if (!when) return null;
  const t = new Date(when).getTime();
  return Number.isFinite(t) ? Math.floor((now - t) / day) : null;
}

/** "in 20 minutes", "in 3 hours". Short, because it sits in a sentence. */
function inWords(ms) {
  const mins = Math.max(1, Math.round(ms / 60000));
  if (mins < 60) return `in ${mins} minute${mins === 1 ? "" : "s"}`;
  const hrs = Math.round(mins / 60);
  return `in ${hrs} hour${hrs === 1 ? "" : "s"}`;
}

/** "Tomorrow at 6:00 AM", in the time zone of the device the coach is
 *  holding: a call's time is only useful in the coach's own clock
 *  (Coleman, 2026-10-07). Null when there's no valid time. */
export function localCallTime(startsAt, now = Date.now()) {
  const at = new Date(startsAt);
  if (!startsAt || Number.isNaN(at.getTime())) return null;
  const midnight = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };
  const days = Math.round((midnight(at) - midnight(now)) / day);
  let time = "";
  try { time = at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); } catch { return null; }
  if (days === 0) return `Today at ${time}`;
  if (days === 1) return `Tomorrow at ${time}`;
  try { return `${at.toLocaleDateString(undefined, { weekday: "long" })} at ${time}`; } catch { return null; }
}

/** "18m", "22h". The face has room for three characters. */
function shortSpan(ms) {
  const mins = Math.max(1, Math.round(ms / 60000));
  return mins < 60 ? `${mins}m` : `${Math.round(mins / 60)}h`;
}

const isFresh = (when, now) => {
  const d = daysSince(when, now);
  return d == null || (d >= 0 && d <= NEW_FOR_DAYS);
};

/**
 * Rank every candidate the facts support, best first.
 *
 * Returns the whole list rather than one winner, because the page draws a
 * deck. The caller filters out what the coach has already skipped today.
 *
 * `facts` is all optional. A missing fact never throws and never invents a
 * candidate, which matters because this runs on the first paint, before most
 * of the dashboard's queries have come back. A half loaded page shows fewer
 * cards, never a wrong one.
 */
export function rankNextActions(facts = {}, now = Date.now()) {
  const {
    call,              // { id, title, startsAt, joinUrl } or null
    billing,           // "ok" | "past_due" | "restricted"
    profileIsEmpty,
    startHere,         // { remaining: [{id,title,to}], total, done } or null
    fresh,             // { masterclasses: [{key,title,sub,art,addedAt}], xo, drills: {count,to,label} }
    qa,                // { id, title, seen, publishedAt } or null
    drop,              // { id, title (the optional focus), summary, publishedAt, seen, dismissed } or null
    unexplored,        // a platformMap entry, or null (the caller owns the cooldown)
    cert,              // { purchased, certified, unitTitle, unitsDone, unitsTotal, lastAt } or null
    resumable,         // [{ key, title, pagesLeft, pagesRead, pct, art, to, moduleKey }]
    joinedAt,
    filmTeam,          // { started } for a remote film intern, or null
  } = facts;

  const out = [];
  const age = daysSince(joinedAt, now);
  const settlingIn = age != null && age < SETTLING_IN_DAYS;
  const untilCall = call && call.startsAt ? new Date(call.startsAt).getTime() - now : NaN;

  /* ── 0. This week's drop, on top of everything until it's opened or closed ──
     Coleman, 2026-10-22: "the weekly drop should persist until they see it
     or x it out. it's immensley important." No time limit and no skipping
     it for the day: the newest drop leads the deck until the coach opens it
     or closes it with its X, and a newer drop takes its place. */
  if (drop && drop.id && !drop.seen && !drop.dismissed && drop.publishedAt && new Date(drop.publishedAt).getTime() <= now) {
    out.push({
      id: `drop:${drop.id}`, kind: "drop", sticky: true, dismissable: true,
      eyebrow: "This week's drop",
      title: drop.summary || "This week's drop is up",
      why: drop.title ? `Focus: ${drop.title}` : null,
      cta: "Open it", to: "drop",
    });
  }

  /* ── 0b. A remote film intern starts on the Film Desk ─────────────────
     Coleman, 2026-11-02: "if they come in through the intern invite... tell them
     in the onboarding / after tour, to go to film desk to get started." It stays
     until they've opened the Film Desk once. */
  if (filmTeam && !filmTeam.started) {
    out.push({
      id: "film-team", kind: "filmdesk", sticky: true,
      eyebrow: "You're on the film team",
      title: "Start on the Film Desk",
      why: "Your quests, and how to find the clips, are waiting there.",
      cta: "Open Film Desk", to: "filmdesk",
    });
  }

  /* ── 1. Money, which quietly stops everything else from working ──────── */
  if (billing === "restricted" || billing === "past_due") {
    out.push({
      id: "billing", kind: "billing", sticky: true, urgent: true,
      eyebrow: "Payment failed",
      title: billing === "restricted"
        ? "Your account is read only"
        : "We could not take your payment",
      why: billing === "restricted"
        ? "Saving and creating are off until the card goes through."
        : "Everything still works. Update the card and this goes away.",
      cta: "Fix it", to: "billing",
    });
  }

  /* ── 2. Week one has its own answer, and it is a list, not a guess ───── */
  if (settlingIn && startHere && !startHere.done && (startHere.remaining || []).length) {
    const next = startHere.remaining[0];
    const left = startHere.remaining.length;
    const total = Number(startHere.total) || left;
    const step = Math.max(1, total - left + 1);
    out.push({
      id: `start-here:${next.id}`, kind: "start-here",
      eyebrow: "Start here",
      title: next.title,
      why: left === 1 ? "Last one, and then you have seen the whole place." : null,
      cta: next.cta || "Do it", to: next.to || "dashboard",
      face: { type: "ring", pct: (step - 1) / total, big: `${step}/${total}`, small: "steps" },
    });
  }

  /* ── 3. New, and only while it is actually new ───────────────────────── */
  for (const m of (fresh && fresh.masterclasses) || []) {
    if (!m || !m.key || !m.title || !isFresh(m.addedAt, now)) continue;
    out.push({
      id: `new-mc:${m.key}`, kind: "new-masterclass",
      eyebrow: "New masterclass",
      title: m.title,
      why: m.sub || null,
      cta: "Open it", to: "master", moduleKey: m.key, art: m.art,
    });
  }

  if (qa && qa.id && !qa.seen && qa.title && isFresh(qa.publishedAt, now)) {
    out.push({
      id: `qa:${qa.id}`, kind: "qa",
      eyebrow: "New Q and A",
      title: qa.title,
      why: null,
      cta: "Read it", to: "qa",
    });
  }

  const xo = Number(fresh && fresh.xo);
  if (Number.isFinite(xo) && xo > 0) {
    out.push({
      id: `new-xo:${xo}`, kind: "new-xo",
      // The face says how many. The title says what, so it never repeats it.
      eyebrow: "Since you last looked",
      title: xo === 1 ? "A new X & O breakdown" : "New X & O breakdowns",
      why: null,
      cta: "Watch", to: "xo",
      face: { type: "count", big: String(xo), small: "new" },
    });
  }

  const drills = fresh && fresh.drills;
  const nDrills = Number(drills && drills.count);
  if (Number.isFinite(nDrills) && nDrills >= DRILL_BATCH_MIN) {
    out.push({
      id: `new-drills:${nDrills}`, kind: "new-drills",
      eyebrow: "Since you last looked",
      title: drills.label ? `New in ${drills.label}` : "New drills",
      why: null,
      cta: "See them", to: drills.to || "pd",
      face: { type: "count", big: String(nDrills), small: "new" },
    });
  }

  /* ── 4. A call in the next day: urgent inside half an hour ───────────── */
  if (Number.isFinite(untilCall) && untilCall > 0 && untilCall <= CALL_URGENT_MS) {
    out.push({
      id: `call:${call.id || call.startsAt}`, kind: "call", sticky: true, urgent: true,
      startsAt: call.startsAt,
      eyebrow: `Starts ${inWords(untilCall)}`,
      title: call.title || "Your next call",
      why: localCallTime(call.startsAt, now),
      cta: "Join", to: call.joinUrl || "calls",
      face: { type: "count", big: shortSpan(untilCall), small: "to go" },
    });
  }

  if (Number.isFinite(untilCall) && untilCall > CALL_URGENT_MS && untilCall <= CALL_SOON_MS) {
    out.push({
      id: `call-soon:${call.id || call.startsAt}`, kind: "call",
      startsAt: call.startsAt,
      eyebrow: `${inWords(untilCall).replace("in ", "In ")}`,
      title: call.title || "Your next call",
      why: localCallTime(call.startsAt, now),
      cta: "See it", to: "calls",
      face: { type: "count", big: shortSpan(untilCall), small: "to go" },
    });
  }

  /* ── 5. Nothing here can be personal without this ────────────────────── */
  if (profileIsEmpty) {
    out.push({
      id: "profile", kind: "setup",
      eyebrow: "One minute",
      title: "Complete your profile",
      why: null,
      cta: "Add it", to: "profile",
    });
  }

  /* ── 6. Certification, once it has gone quiet ────────────────────────── */
  if (cert && cert.purchased && !cert.certified && cert.unitTitle) {
    const quiet = daysSince(cert.lastAt, now);
    if (quiet == null || quiet >= CERT_QUIET_DAYS) {
      const done = Number(cert.unitsDone), total = Number(cert.unitsTotal);
      const left = Number.isFinite(done) && Number.isFinite(total) ? Math.max(0, total - done) : NaN;
      out.push({
        id: "cert", kind: "cert",
        eyebrow: "Certification",
        title: cert.unitTitle,
        why: Number.isFinite(left) && left > 0
          ? `${left} unit${left === 1 ? "" : "s"} to go.`
          : null,
        cta: "Carry on", to: "cert",
        ...(Number.isFinite(done) && total > 0
          ? { face: { type: "ring", pct: done / total, big: `${done}/${total}`, small: "units" } }
          : {}),
      });
    }
  }

  /* ── 7. Something half read ──────────────────────────────────────────── */
  for (const r of resumable || []) {
    if (!r || !r.title) continue;
    const left = Number(r.pagesLeft);
    let why, face;
    if (Number.isFinite(left)) {
      if (left <= 0) continue;
      const read = Number(r.pagesRead);
      if (Number.isFinite(read) && read < RESUME_MIN_PAGES) continue;
      why = left === 1 ? "One page from the end." : `${left} pages from the end.`;
      if (Number.isFinite(read)) {
        face = { type: "ring", pct: read / (read + left), big: String(left), small: left === 1 ? "page left" : "pages left" };
      }
    } else {
      // Older progress only kept a percentage, in one browser. Still worth
      // a card, but a few percent is a page somebody landed on, not a start.
      const pct = Number(r.pct);
      if (!Number.isFinite(pct) || pct < RESUME_MIN_PCT || pct >= 100) continue;
      why = null;
      face = { type: "ring", pct: pct / 100, big: `${Math.round(pct)}%`, small: "read" };
    }
    out.push({
      id: `resume:${r.key}`, kind: "resume",
      eyebrow: "Pick back up",
      title: `Finish ${r.title}`,
      why,
      cta: "Open it", to: r.to, art: r.art, moduleKey: r.moduleKey,
      ...(face ? { face } : {}),
    });
  }

  /* ── 8. A door they have never opened. Never in week one ─────────────── */
  // Last, and below anything with a date or progress in it: it is the one
  // card that is never about today.
  if (!settlingIn && unexplored && unexplored.id) {
    out.push({
      id: `unexplored:${unexplored.id}`, kind: "unexplored",
      eyebrow: "You have not opened this",
      title: unexplored.label || unexplored.what || unexplored.id,
      why: unexplored.when || unexplored.what || null,
      cta: "Take a look", to: unexplored.id,
    });
  }

  return out;
}

/**
 * The deck to draw: what the coach has skipped is gone, and sticky cards
 * cannot be skipped however hard anyone tries.
 *
 * Skipping is per id, so skipping "three pages from the end" does not skip
 * every masterclass forever, and the card comes back tomorrow with a
 * different number in it.
 */
export function visibleActions(ranked, skipped = []) {
  const gone = new Set(skipped || []);
  return (ranked || []).filter(a => a.sticky || !gone.has(a.id));
}

/** The one on top, or null when the slot should not render at all. */
export function topAction(ranked, skipped = []) {
  return visibleActions(ranked, skipped)[0] || null;
}

/**
 * Should the "never opened" card be allowed today?
 *
 * Coleman: "only show like once every few days". It gets a day, then rests
 * for UNEXPLORED_REST_DAYS. `lastShownDay` is the day it last appeared
 * (a toDateString), owned by the caller's storage.
 */
export const UNEXPLORED_REST_DAYS = 3;
export function unexploredAllowed(lastShownDay, now = Date.now()) {
  if (!lastShownDay) return true;
  const today = new Date(now).toDateString();
  if (lastShownDay === today) return true;
  const last = new Date(lastShownDay).getTime();
  if (!Number.isFinite(last)) return true;
  return (now - last) / day >= UNEXPLORED_REST_DAYS;
}
